import { memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import type { Node, NodeProps } from '@xyflow/react';
import type { ActionNodeData } from '@/utils/ontologyTree/types';

export type ActionFlowNode = Node<ActionNodeData, 'action'>;

const ACTION_STYLES: Record<
  ActionNodeData['actionType'],
  { bg: string; border: string; color: string; bgHover: string; icon: string }
> = {
  show_more_genes: { bg: '#f3e5f5', border: '#9c27b0', color: '#6a1b9a', bgHover: '#e1bee7', icon: '＋' },
  other_genes: { bg: '#fbe9e7', border: '#795548', color: '#4e342e', bgHover: '#ffccbc', icon: '⋯' },
  show_related_diseases: {
    bg: '#e8f5e9',
    border: '#388e3c',
    color: '#1b5e20',
    bgHover: '#c8e6c9',
    icon: '⤵',
  },
  more_children: { bg: '#e8f0ff', border: '#4a90d9', color: '#2c6fad', bgHover: '#c5d8ff', icon: '↓' },
};

export const ActionNode = memo(function ActionNode({ data }: NodeProps<ActionFlowNode>) {
  const { label, actionType, onClick } = data;
  const s = ACTION_STYLES[actionType];
  const canExpand = actionType === 'show_related_diseases' || actionType === 'more_children';

  return (
    <div
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={e => e.key === 'Enter' && onClick()}
      style={{
        background: s.bg,
        border: `1.5px dashed ${s.border}`,
        borderRadius: 8,
        padding: '5px 12px',
        fontSize: 11,
        fontWeight: 600,
        color: s.color,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        whiteSpace: 'nowrap',
        userSelect: 'none',
        transition: 'background 0.15s',
      }}
      onMouseOver={e => (e.currentTarget.style.background = s.bgHover)}
      onMouseOut={e => (e.currentTarget.style.background = s.bg)}
    >
      <Handle type="target" position={Position.Left} style={{ background: s.border, width: 7, height: 7 }} />

      <span aria-hidden="true">{s.icon}</span>
      {label}

      {canExpand && (
        <Handle
          type="source"
          position={Position.Right}
          style={{ background: s.border, width: 7, height: 7 }}
        />
      )}
    </div>
  );
});
